import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { Loader2 } from "lucide-react";

const LANG_COLOR = {
  en: "#1c1a17",
  "zh-HK": "#8a3a33",
  "zh-TW": "#b86b5d",
  "zh-CN": "#5c564c",
};
const LANG_LABEL = { en: "EN", "zh-HK": "繁中·港", "zh-TW": "繁中·台", "zh-CN": "简中" };

export default function ConversionTimeline({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/conversion-timeline?days=7", { headers: { "X-Admin-Token": token } })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="border border-[var(--rule)] p-10 flex items-center text-[var(--ink-soft)] italic" data-testid="timeline-loading">
        <Loader2 className="animate-spin mr-3" /> drawing the trend lines…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="border border-[var(--rule)] p-10 text-[var(--wax-deep)] italic" data-testid="timeline-error">
        {error || "No data"}
      </div>
    );
  }

  // Flatten series into recharts-friendly rows: { date, en, "zh-HK", "zh-TW", "zh-CN" }
  const rows = data.series.map((d) => {
    const out = { date: d.date.slice(5) };
    for (const code of data.languages) out[code] = d.languages[code]?.rate || 0;
    return out;
  });

  const empty = data.days_with_data === 0;

  return (
    <div data-testid="timeline-chart">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-display text-xl tracking-tight">7-day conversion trend · by language</h2>
          <p className="text-[13px] text-[var(--ink-soft)] italic">
            {data.threshold_met
              ? "Threshold met — watch which lines lift after each campaign."
              : `Collecting data · ${data.days_with_data}/7 days populated`}
          </p>
        </div>
        <span
          data-testid="timeline-status"
          className={`text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
            data.threshold_met
              ? "bg-[var(--wax)] text-[var(--bg-paper)]"
              : "border border-[var(--rule)] text-[var(--ink-soft)]"
          }`}
        >
          {data.threshold_met ? "live" : "warming up"}
        </span>
      </div>

      <div className="border border-[var(--rule)] bg-[var(--bg-paper-deep)]/40 p-5">
        {empty ? (
          <div className="py-16 text-center text-[var(--ink-soft)] italic" data-testid="timeline-empty">
            No visits or sealed letters in the last 7 days yet.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={rows} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="#e2dcd0" strokeDasharray="2 4" />
              <XAxis
                dataKey="date"
                tick={{ fill: "#5c564c", fontSize: 12, fontFamily: "Cormorant Garamond" }}
                stroke="#c5beb0"
              />
              <YAxis
                tickFormatter={(v) => `${v}%`}
                tick={{ fill: "#5c564c", fontSize: 12 }}
                stroke="#c5beb0"
                width={48}
              />
              <Tooltip
                contentStyle={{
                  background: "#f9f6f0",
                  border: "1px solid #e2dcd0",
                  borderRadius: 0,
                  fontFamily: "Cormorant Garamond, serif",
                }}
                formatter={(v, name) => [`${v}%`, LANG_LABEL[name] || name]}
              />
              <Legend
                formatter={(v) => LANG_LABEL[v] || v}
                wrapperStyle={{ fontFamily: "Cormorant Garamond, serif", fontSize: 14 }}
              />
              {data.languages.map((code) => (
                <Line
                  key={code}
                  type="monotone"
                  dataKey={code}
                  stroke={LANG_COLOR[code] || "#1c1a17"}
                  strokeWidth={2}
                  dot={{ r: 3, fill: LANG_COLOR[code] }}
                  activeDot={{ r: 5 }}
                  isAnimationActive
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
